import { useState, useEffect, useCallback } from 'react'
import { Search, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Activity, FileSearch } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { toast } from 'sonner'
import { getAdminClientApiLogs, type AdminClientApiLogRow, type AdminClientApiLogParams } from '@/src/lib/api/admin-analytics'
import { AdminApiLogDetailModal } from './AdminApiLogDetailModal'

const TIME_RANGES = [
  { label: 'วันนี้', value: 'today' },
  { label: '7 วัน', value: 'week' },
  { label: '30 วัน', value: 'month' },
  { label: 'ทั้งหมด', value: 'all' },
]

function getDateRange(range: string): { startDate?: string; endDate?: string } {
  const now = new Date()
  if (range === 'today') {
    const start = new Date(now); start.setHours(0, 0, 0, 0)
    return { startDate: start.toISOString() }
  }
  if (range === 'week') {
    const start = new Date(now); start.setDate(start.getDate() - 7)
    return { startDate: start.toISOString() }
  }
  if (range === 'month') {
    const start = new Date(now); start.setDate(start.getDate() - 30)
    return { startDate: start.toISOString() }
  }
  return {}
}

function StatusBadge({ code }: { code: number }) {
  if (code >= 200 && code < 300)
    return <Badge className="bg-green-100 text-green-800 border-green-200">{code}</Badge>
  if (code >= 400 && code < 500)
    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">{code}</Badge>
  if (code >= 500)
    return <Badge className="bg-red-100 text-red-800 border-red-200">{code}</Badge>
  return <Badge variant="secondary">{code}</Badge>
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-800',
    POST: 'bg-purple-100 text-purple-800',
    PUT: 'bg-orange-100 text-orange-800',
    DELETE: 'bg-red-100 text-red-800',
    PATCH: 'bg-teal-100 text-teal-800',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold ${colors[method] || 'bg-gray-100 text-gray-700'}`}>
      {method}
    </span>
  )
}

export default function AdminApiActivity() {
  const [rows, setRows] = useState<AdminClientApiLogRow[]>([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [timeRange, setTimeRange] = useState('week')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('api')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const LIMIT = 50

  const fetchData = useCallback(async (p = 1, search = appliedSearch) => {
    setLoading(true)
    try {
      const dateRange = getDateRange(timeRange)
      const params: AdminClientApiLogParams = {
        page: p,
        limit: LIMIT,
        sortOrder: 'DESC',
        searchTerm: search || undefined,
        ...dateRange,
      }
      if (statusFilter !== 'all') params.status = statusFilter as 'success' | 'error'
      if (sourceFilter !== 'all') params.source = sourceFilter as 'api' | 'web'

      const res = await getAdminClientApiLogs(params)
      setRows(res.data ?? [])
      setTotalPages(res.pagination?.totalPages ?? 1)
      setTotal(res.pagination?.total ?? 0)
    } catch (err: any) {
      toast.error(err.message || 'โหลดข้อมูลล้มเหลว')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [timeRange, statusFilter, sourceFilter, appliedSearch])

  useEffect(() => { setPage(1); fetchData(1) }, [timeRange, statusFilter, sourceFilter])
  useEffect(() => { fetchData(page) }, [page])

  const handleSearch = () => {
    setAppliedSearch(searchInput)
    setPage(1)
    fetchData(1, searchInput)
  }

  const handleRefresh = () => { setPage(1); fetchData(1) }

  const goTo = (p: number) => { if (p >= 1 && p <= totalPages) setPage(p) }

  const formatDate = (s: string) => {
    try { return format(new Date(s), 'dd MMM yy HH:mm', { locale: th }) } catch { return s }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ประวัติการเรียกใช้งาน API</h1>
          <p className="text-muted-foreground">บันทึกการใช้งาน API ของลูกค้าทุกราย</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          รีเฟรช
        </Button>
      </div>

      {/* Filters Row */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="flex gap-2 flex-1 min-w-[240px] max-w-md">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Endpoint, อีเมล, IP Address..."
                  className="pl-9"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <Button variant="secondary" onClick={handleSearch} disabled={loading}>ค้นหา</Button>
            </div>

            {/* Time Range */}
            <div className="flex gap-1 flex-wrap">
              {TIME_RANGES.map(t => (
                <button
                  key={t.value}
                  onClick={() => setTimeRange(t.value)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    timeRange === t.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Status */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="สถานะ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกสถานะ</SelectItem>
                <SelectItem value="success">สำเร็จ (2xx)</SelectItem>
                <SelectItem value="error">ผิดพลาด (4xx/5xx)</SelectItem>
              </SelectContent>
            </Select>

            {/* Source: real API key calls vs. dashboard (web) calls */}
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-44" aria-label="แหล่งที่มาของการเรียก">
                <SelectValue placeholder="แหล่งที่มา" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="api">เรียกผ่าน API (API Key)</SelectItem>
                <SelectItem value="web">เรียกจากหน้าเว็บ</SelectItem>
                <SelectItem value="all">ทั้งหมด</SelectItem>
              </SelectContent>
            </Select>

          </div>
        </CardContent>
      </Card>

      {/* Summary bar */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          <span>พบ <span className="font-semibold text-foreground">{total.toLocaleString()}</span> รายการ</span>
        </div>
        <span>หน้า {page} / {totalPages}</span>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-40">วันที่-เวลา</TableHead>
                <TableHead>ลูกค้า</TableHead>
                <TableHead>Method / Endpoint</TableHead>
                <TableHead className="w-28 text-center">Bank API</TableHead>
                <TableHead className="w-24 text-center">Status</TableHead>
                <TableHead className="w-28 text-right">Response Time</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Error</TableHead>
                <TableHead className="w-28 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-muted animate-pulse rounded" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                    ไม่พบข้อมูล API activity ในช่วงเวลาที่เลือก
                  </TableCell>
                </TableRow>
              ) : (
                rows.map(row => (
                  <TableRow key={row.id} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium truncate max-w-[160px]" title={row.customer_name ?? ''}>
                        {row.customer_name || '—'}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[160px]" title={row.customer_email ?? ''}>
                        {row.customer_email || ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <MethodBadge method={row.http_method} />
                        <span className="font-mono text-xs text-foreground truncate max-w-[280px]" title={row.endpoint ?? ''}>
                          {row.endpoint || '—'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.bank_code ? (
                        <Badge variant="outline" className="font-mono text-xs">{row.bank_code}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <StatusBadge code={row.response_status ?? 0} />
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      <span className={(row.processing_time_ms ?? 0) > 2000 ? 'text-red-600 font-medium' : (row.processing_time_ms ?? 0) > 800 ? 'text-yellow-600' : 'text-muted-foreground'}>
                        {row.processing_time_ms != null ? `${row.processing_time_ms.toLocaleString()} ms` : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.ip_address || '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      {(row.response_status !== null && row.response_status >= 400 && row.error_code) && (
                        <span className="text-xs text-red-600 font-mono" title={row.error_message ?? ''}>
                          {row.error_code}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedLogId(row.id)
                          setIsDetailOpen(true)
                        }}
                      >
                        <FileSearch className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => goTo(1)} disabled={page === 1}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => goTo(page - 1)} disabled={page === 1}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {[...Array(Math.min(5, totalPages))].map((_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4))
            const p = start + i
            return (
              <Button
                key={p}
                variant={p === page ? 'default' : 'outline'}
                size="sm"
                className="w-9"
                onClick={() => goTo(p)}
              >
                {p}
              </Button>
            )
          })}
          <Button variant="outline" size="sm" onClick={() => goTo(page + 1)} disabled={page === totalPages}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => goTo(totalPages)} disabled={page === totalPages}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Detail Modal */}
      <AdminApiLogDetailModal
        logId={selectedLogId}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
      />
    </div>
  )
}
